import { httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { functions } from '../firebase';
import { Timestamp } from 'firebase/firestore';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import type { DecisionResponse } from '../types/decision';

interface ResponsePointer {
  interimResponseId: string | null;
  finalResponseId: string | null;
  status: 'idle' | 'processing' | 'failed';
  lastUpdateAttempt: Timestamp;
  error?: string;
}

if (window.location.hostname === 'localhost') {
  console.log('Running on localhost - connecting to Functions emulator...');
  try {
    if (!functions) {
      throw new Error('Functions instance not initialized');
    }
    connectFunctionsEmulator(functions, 'localhost', 5001);
    console.log('Successfully connected to Functions emulator');
  } catch (error) {
    console.error('Failed to connect to Functions emulator:', error);
    if (error instanceof Error) {
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
    }
  }
}

export const getResponseStatus = async (decisionId: string): Promise<ResponsePointer> => {
  try {
    const pointerRef = doc(db, `decisions/${decisionId}/responses/pointer`);
    const pointerSnap = await getDoc(pointerRef);
    
    if (!pointerSnap.exists()) {
      return {
        interimResponseId: null,
        finalResponseId: null,
        status: 'idle',
        lastUpdateAttempt: Timestamp.now()
      };
    }

    const data = pointerSnap.data();
    
    // Handle legacy data format
    if ('currentResponseId' in data) {
      return {
        interimResponseId: data.currentResponseId,
        finalResponseId: null,
        status: data.status,
        lastUpdateAttempt: data.lastUpdateAttempt,
        error: data.error
      };
    }

    return {
      interimResponseId: data.interimResponseId,
      finalResponseId: data.finalResponseId,
      status: data.status,
      lastUpdateAttempt: data.lastUpdateAttempt,
      error: data.error
    };
  } catch (error) {
    console.error('Error getting response status:', error);
    throw error;
  }
};

export interface DecisionContext {
  question: string;
  details?: string;
  latestSynthesis?: string | null;
}

export const generateChatResponse = async (data: {
  decisionId: string;
  participantId: string;
  message: string;
  decisionContext: {
    question: string;
    details?: string;
    latestSynthesis?: string | null;
  };
  messageHistory?: Array<{role: 'user' | 'model', content: string}>;
}) => {
  try {
    console.log('generateChatResponse: Starting function call with data:', data);
    
    if (!functions) {
      throw new Error('Functions instance not initialized');
    }
    
    const chatFunction = httpsCallable(functions, 'generateChatResponse');
    console.log('generateChatResponse: Created callable function');
    
    // Add retry logic with exponential backoff
    const MAX_RETRIES = 3;
    let retryCount = 0;
    let lastError: unknown = null;

    while (retryCount < MAX_RETRIES) {
      try {
        if (retryCount > 0) {
          console.log(`generateChatResponse: Retry attempt ${retryCount} of ${MAX_RETRIES}`);
        }
        
        const result = await chatFunction(data);
        console.log('generateChatResponse: Received result:', result.data);
        return result.data;
      } catch (error: unknown) {
        lastError = error;
        
        // Check if this is a rate limit error
        const isRateLimitError = 
          error && 
          typeof error === 'object' && 
          (
            ('code' in error && (error as any).code === 'resource-exhausted') ||
            (error instanceof Error && error.message.includes('resource-exhausted')) ||
            (error instanceof Error && error.message.includes('rate limit'))
          );
        
        if (isRateLimitError && retryCount < MAX_RETRIES - 1) {
          // Calculate backoff time: 2^retryCount * 1000ms (1s, 2s, 4s, etc.)
          const backoffMs = Math.pow(2, retryCount) * 1000;
          const retryAfterMs = ('details' in (error as any) && (error as any).details?.retryAfter) 
            ? (error as any).details.retryAfter * 1000
            : backoffMs;
            
          console.log(`generateChatResponse: Rate limit detected, retrying in ${retryAfterMs/1000}s...`);
          
          // Wait for the backoff period
          await new Promise(resolve => setTimeout(resolve, retryAfterMs));
          
          // Increment retry counter and try again
          retryCount++;
          continue;
        }
        
        // For non-rate limit errors or if we've exceeded retries, just throw
        console.error('Error in generateChatResponse:', error);
        throw error;
      }
    }
    
    // If we get here, we've exceeded our retries
    console.error('Max retries exceeded in generateChatResponse:', lastError);
    throw lastError;
  } catch (error: unknown) {
    // Add more detailed error handling
    if (error && typeof error === 'object') {
      // Format user-friendly error messages
      if ('code' in error) {
        const code = (error as any).code;
        if (code === 'resource-exhausted') {
          throw new Error("You've sent too many messages. Please wait a moment and try again.");
        } else if (code === 'unavailable') {
          throw new Error('The service is temporarily unavailable. Please try again in a few minutes.');
        }
      }
      
      // Add specific error logging to help with debugging
      if ('code' in error) console.error('Error code:', (error as { code: string }).code);
      if ('message' in error) console.error('Error message:', (error as { message: string }).message);
      if ('details' in error) console.error('Error details:', (error as { details: unknown }).details);
    }
    
    throw error;
  }
};

export const refreshDecisionResponse = async (decisionId: string): Promise<DecisionResponse> => {
  try {
    console.log('refreshDecisionResponse: Starting function call for decision:', decisionId);
    
    const synthesisFunction = httpsCallable(functions, 'synthesizeDecisionResponses');
    const result = await synthesisFunction({ decisionId });
    const responseData = result.data as any;
    
    // Convert the timestamp data to a Firestore Timestamp
    return {
      ...responseData,
      // Need to handle both server and client timestamp formats
      lastUpdated: new Timestamp(
        responseData.lastUpdated._seconds || responseData.lastUpdated.seconds,
        responseData.lastUpdated._nanoseconds || responseData.lastUpdated.nanoseconds
      )
    } as DecisionResponse;
  } catch (error: unknown) {
    console.error('Error in refreshDecisionResponse:', error);
    throw error;
  }
};

